import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { crmLeadFeatures } from "../../data/crmPageContent";
import { CrmDashboardMockup } from "./CrmMockups";
import { CrmReveal } from "./CrmMotion";

const dashboardVariants: ("leads" | "customers" | "sales" | "reports")[] = ["leads", "customers", "sales", "reports", "leads"];

const featureBenefits: Record<string, string[]> = {
    "Lead Capture": [
        "Capture leads from website, WhatsApp, and forms",
        "Automatically assign source and campaign data",
        "Create instant lead records without manual entry",
    ],
    "Lead Assignment": [
        "Route leads to the right rep in real time",
        "Use round-robin or rule-based assignment",
        "Balance team workload with smart distribution",
    ],
    "Lead Tracking": [
        "Track every interaction in one timeline",
        "Monitor lead stage movement and response speed",
        "See complete customer journey across channels",
    ],
    "Lead Qualification": [
        "Score leads by intent and engagement",
        "Prioritize high-fit prospects automatically",
        "Focus team effort on opportunities most likely to close",
    ],
    "Lead Conversion": [
        "Move qualified leads into deal workflows",
        "Use guided follow-up tasks and reminders",
        "Track conversion metrics from first touch to win",
    ],
};

export const CrmLeadSection = () => {
    const [active, setActive] = useState(0);
    const current = crmLeadFeatures[active];
    const Icon = current.icon;

    return (
        <section className="crm-leads" id="crm-leads">
            <div className="crm-container">
                <CrmReveal className="crm-leads__intro">
                    <h2 className="crm-h2">Lead Management Built for Growth</h2>
                    <p className="crm-lead">
                        Capture, qualify, and convert leads with a system designed to keep your pipeline full and your team focused.
                    </p>
                </CrmReveal>

                <div className="crm-leads__layout">
                    <div className="crm-leads__tabs">
                        {crmLeadFeatures.map((item, i) => {
                            const TabIcon = item.icon;
                            return (
                                <button
                                    key={item.title}
                                    type="button"
                                    className={`crm-leads__tab ${active === i ? "crm-leads__tab--on" : ""}`}
                                    onClick={() => setActive(i)}
                                >
                                    <TabIcon size={18} />
                                    <span>{item.title}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="crm-leads__preview-wrap">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.title}
                                className="crm-leads__panel"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <div className="crm-leads__panel-copy">
                                    <div className="crm-leads__panel-icon">
                                        <Icon size={24} />
                                    </div>
                                    <h3>{current.title}</h3>
                                    <p>{current.description}</p>
                                    <ul className="crm-leads__benefits">
                                        {(featureBenefits[current.title] ?? []).map((benefit) => (
                                            <li key={benefit}>{benefit}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="crm-leads__panel-visual">
                                    <CrmDashboardMockup variant={dashboardVariants[active]} />
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};
